'use client';
import React, { useState, useEffect } from 'react';
import { type ArticleBodyBlock } from '@/lib/types';
import Image from 'next/image';
import Script from 'next/script';
import { Camera, Clock } from 'lucide-react';
import Link from 'next/link';
import { doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '@/firebase';
import { isVideoUrl, parseDate } from '@/lib/utils';
import { format } from 'date-fns';
import { AdUnit } from './ad-unit';

const parseText = (text: string = '') => {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline font-bold" target="_blank" rel="noopener noreferrer">$1</a>');
};

function HydratedUpdateDate({ date }: { date: string }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return <span className="opacity-0">...</span>;
  return <span>{format(parseDate(date), 'HH:mm • MMMM d, yyyy')}</span>;
}

function Quote({ text }: { text: string }) {
  return (
    <blockquote className="my-10 pl-6 border-l-4 border-primary italic">
      <p className="text-xl md:text-2xl font-bold leading-relaxed text-foreground/90">
        <span dangerouslySetInnerHTML={{ __html: parseText(text) }} />
      </p>
    </blockquote>
  );
}

function Heading({ text }: { text: string }) {
  return <h2 className="text-2xl md:text-2xl font-bold mt-12 mb-6 tracking-tight">{text}</h2>;
}

function TableBlock({ block }: { block: ArticleBodyBlock }) {
  if (!block.columns || !block.rows) return null;

  return (
    <div className="my-12 w-full">
      {block.caption && (
        <div className="mb-4 text-[10px] font-black uppercase tracking-[0.2em] text-foreground flex items-center gap-2">
          <span className="h-3 w-0.5 bg-primary"></span>
          {block.caption}
        </div>
      )}
      <div className="overflow-x-auto rounded-2xl border border-foreground/20 bg-foreground/[0.02] shadow-sm">
        <table className="w-full text-left border-collapse min-w-[500px]">
          <thead>
            <tr className="bg-foreground/[0.03] border-b border-foreground/20">
              {block.columns.map((col) => (
                <th key={col} className="px-6 py-4 text-sm font-bold text-foreground uppercase tracking-tight">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-foreground/20">
            {block.rows.map((row, i) => (
              <tr key={i} className="hover:bg-foreground/[0.01] transition-colors">
                {block.columns!.map((col) => (
                  <td key={col} className="px-6 py-5 text-sm font-normal text-foreground/90 leading-relaxed">
                    {row[col]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinkedArticle({ url, type }: { url: string; type: string }) {
  const [headline, setHeadline] = useState<string | null>(null);
  const { firestore } = useFirebase();

  useEffect(() => {
    if (!url || !firestore) return;
    
    let slug = '';
    try {
      if (url.startsWith('http')) {
        const urlObj = new URL(url);
        const segments = urlObj.pathname.split('/').filter(Boolean);
        slug = segments[segments.length - 1];
      } else {
        const segments = url.split('/').filter(Boolean);
        slug = segments[segments.length - 1];
      }
      
      if (slug) {
        const docRef = doc(firestore, 'articles', slug.toLowerCase());
        getDoc(docRef).then(snap => {
          if (snap.exists()) {
            setHeadline(snap.data()?.headline || snap.data()?.title || null);
          }
        });
      }
    } catch (e) {
      console.error("Linked Article Error:", e);
    }
  }, [url, firestore]);

  if (!headline) return null;

  return (
    <div className="my-8 pl-6 border-l-4 border-primary">
      <p className="text-lg leading-relaxed m-0 text-foreground/90 font-medium">
        <span className="font-black text-primary uppercase tracking-tight">| {type} : </span>
        <Link 
          href={url} 
          className="hover:text-primary transition-colors no-underline"
        >
          {headline}
        </Link>
      </p>
    </div>
  );
}

function EmbedBlock({ url, caption }: { url: string; caption?: string }) {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      const videoId = hostname.includes('youtu.be') 
        ? urlObj.pathname.split('/').filter(Boolean)[0] 
        : urlObj.searchParams.get('v');
      
      if (videoId) {
        return (
          <div className="my-10 space-y-4">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-lg bg-black border border-foreground/10">
              <iframe 
                width="100%" 
                height="100%" 
                src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`} 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                allowFullScreen 
                className="w-full h-full"
              ></iframe>
            </div>
            {caption && <p className="text-sm text-muted-foreground italic px-2">{caption}</p>}
          </div>
        );
      }
    }

    if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
      const cleanUrl = url.split('?')[0];
      
      return (
        <div className="my-10 flex flex-col items-center gap-4 w-full">
          <div className="w-full max-w-[550px] flex justify-center">
            <blockquote className="twitter-tweet">
              <a href={cleanUrl}></a>
            </blockquote>
          </div>
          
          <Script 
            src="https://platform.twitter.com/widgets.js" 
            strategy="lazyOnload" 
            onLoad={() => {
              if (window.twttr && window.twttr.widgets) {
                 window.twttr.widgets.load();
              }
            }}
          />

          {caption && <p className="text-sm text-muted-foreground italic text-center">{caption}</p>}
        </div>
      );
    }
  } catch (e) {
    return <p className="mb-6 leading-relaxed italic text-muted-foreground">{url}</p>;
  }

  return null;
}

function VideoBlock({ url, caption }: { url: string; caption?: string }) {
  if (url.includes('x.com') || url.includes('twitter.com') || url.includes('youtube.com') || url.includes('youtu.be')) {
    return <EmbedBlock url={url} caption={caption} />;
  }

  return (
    <div className="my-10 rounded-2xl overflow-hidden shadow-lg bg-black border border-foreground/10">
      <video 
        className="w-full h-auto" 
        playsInline 
        controls
        preload="metadata"
      >
        <source src={url} />
        Your browser does not support the video tag.
      </video>

      {caption && (
        <div className="p-4 bg-accent/30 text-sm text-muted-foreground italic border-t border-foreground/10">
          {caption}
        </div>
      )}
    </div>
  );
}

export function ArticleBody({ body }: { body: ArticleBodyBlock[] }) {
  if (!body || !Array.isArray(body)) return null;

  return (
    <div className="article-content prose prose-lg max-w-none text-foreground/90 leading-relaxed">
      {body.map((block, index) => {
        if (!block) return null;

        return (
          <React.Fragment key={index}>
            {block.type === 'lede' && <p className="text-xl font-medium text-foreground mb-8 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseText(block.text) }} />}
            {(block.type === 'text' || block.type === 'paragraph') && <p className="mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseText(block.text) }} />}
            {block.type === 'heading' && <Heading text={block.text || ''} />}
            {block.type === 'quote' && <Quote text={block.text || ''} />}
            {block.type === 'table' && <TableBlock block={block} />}
            {block.type === 'ad' && (
              <AdUnit 
                type="in-article" 
                slotId={block.text || undefined}
                className="w-full max-w-[728px] mx-auto" 
              />
            )}
            {(block.type === 'Linked Article' || block.type === 'Related Piece:' || block.type === 'Also Read') && block.url && (
              <LinkedArticle url={block.url} type={block.type} />
            )}
            {block.type === 'list' && block.items && (
              <ul className="list-disc list-outside pl-5 space-y-3 mb-8">
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex} dangerouslySetInnerHTML={{ __html: parseText(item) }}/>
                ))}
              </ul>
            )}
            {block.type === 'image' && (block.url || block.text) && (() => {
              const imgSrc = block.url || block.text;
              if (!imgSrc?.startsWith('http')) return null;

              if (isVideoUrl(imgSrc)) {
                return <VideoBlock url={imgSrc} caption={block.caption} />;
              }

              return (
                <div className="my-10 relative w-full aspect-[4/3] md:aspect-video overflow-hidden rounded-2xl shadow-md">
                  <Image 
                    src={imgSrc} 
                    alt={block.caption || "Article content image"} 
                    fill 
                    sizes="(max-width: 768px) 100vw, 800px"
                    className="object-cover" 
                    quality={70}
                  />
                  {(block.caption || block.credit) && (
                    <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/95 via-black/40 to-transparent text-white text-sm md:text-base font-medium">
                      {block.caption && <div className="mb-1 leading-tight">{block.caption}</div>}
                      {block.credit && (
                        <div className="flex items-center gap-1.5 opacity-90">
                          <Camera className="h-4 w-4" />
                          <span>: {block.credit}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {block.type === 'video' && (block.url || block.text) && (
              <VideoBlock url={(block.url || block.text)!} caption={block.caption} />
            )}
            {block.type === 'embed' && (block.url || block.text) && (
              <EmbedBlock url={(block.url || block.text)!} caption={block.caption} />
            )}

            {block.type === 'update' && (
              <div className="my-12 p-8 bg-primary/5 border-l-4 border-primary rounded-r-2xl shadow-sm border-foreground/20">
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <span className="bg-primary text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-sm">
                    {block.label || 'UPDATE'}
                  </span>
                  {block.timestamp && (
                     <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
                       <Clock className="h-3 w-3" />
                       <HydratedUpdateDate date={block.timestamp} />
                     </div>
                  )}
                </div>
                <div className="article-update-content">
                  <ArticleBody body={block.content || []} />
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
